import { z } from 'zod'
import type { AppArtifactBundle } from '../guidance/app-tools/artifact-schema'
import { TOOL_CATALOG } from '../guidance/app-tools/schema'

export const JOURNEY_STEP_TIMINGS = z.object({
  steps: z.array(z.object({ index: z.number().int().nonnegative(), duration_ms: z.number().int().nonnegative().optional() })).optional(),
})

const RUN_TOOL_SNAPSHOT = z.object({
  runId: z.string(), journeyId: z.string(),
  steps: JOURNEY_STEP_TIMINGS.shape.steps,
  invocations: z.array(z.object({
    toolId: z.string(), revision: z.number().int().positive(),
    firstStep: z.number().int().nonnegative(), lastStep: z.number().int().nonnegative(),
    started_at: z.string().datetime().optional(),
    duration_ms: z.number().int().nonnegative().optional(),
  }).refine(value => value.lastStep >= value.firstStep)),
})

function parseJson(text: string | undefined): unknown {
  try { return text ? JSON.parse(text) : null } catch { return null }
}

/** Resolve against the executed snapshot, never the current tool's possibly healed step count. */
export function journeyToolSnapshot(bundle: AppArtifactBundle | undefined, runId: string | undefined) {
  const snapshot = RUN_TOOL_SNAPSHOT.safeParse(parseJson(bundle?.files.find(file => file.path === 'qa-tools/run.json')?.contents))
  if (!snapshot.success || snapshot.data.runId !== runId) return null
  const catalog = TOOL_CATALOG.safeParse(parseJson(bundle?.files.find(file => file.path === 'qa-tools/catalog.json')?.contents))
  return { ...snapshot.data, tools: catalog.success ? catalog.data.tools : [] }
}

export function toolFailureLocation(snapshot: ReturnType<typeof journeyToolSnapshot>, stepIndex: number | undefined) {
  if (!snapshot || stepIndex === undefined) return null
  const invocationIndex = snapshot.invocations.findIndex(call => stepIndex >= call.firstStep && stepIndex <= call.lastStep)
  const invocation = snapshot.invocations[invocationIndex]
  return invocation ? { invocationIndex, stepIndex: stepIndex - invocation.firstStep } : null
}

/** Explain structured terminal outcomes even when the runner did not write a detail string. */
export function journeyStopReason(outcome: { detail?: string; reason?: string; proposedBug?: string | null; judgeProblems?: string[] } | null | undefined): string | null {
  if (outcome?.detail?.trim()) return outcome.detail
  if (outcome?.reason === 'bug-rejected') {
    return ['Recovery reported a possible bug, but it was not confirmed. The journey stopped without completing this step.',
      outcome.proposedBug ? `Reported issue (unconfirmed): ${outcome.proposedBug}` : '',
      ...(outcome.judgeProblems ?? []),
    ].filter(Boolean).join(' ')
  }
  return outcome?.reason ? `The run stopped: ${outcome.reason.replace(/[-_]/g, ' ')}.` : null
}
